import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * DynamicCustomFields — renders typed custom fields for a company as form inputs.
 * Props:
 *   companyId: string
 *   value: object of key -> value
 *   onChange: (nextValue) => void
 * Fields come from GET /api/company-field-schemas
 */
export default function DynamicCustomFields({ companyId, value = {}, onChange }) {
  const [schemas, setSchemas] = useState([]);

  useEffect(() => {
    if (!companyId) { setSchemas([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/company-field-schemas", { params: { company_id: companyId } });
        setSchemas(data);
      } catch { /* ignore */ }
    })();
  }, [companyId]);

  const setKey = (k, v) => onChange({ ...value, [k]: v });

  if (schemas.length === 0) return null;

  return (
    <div className="space-y-2 rounded-xl border border-border p-3 bg-muted/20" data-testid="dynamic-fields-panel">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Custom fields</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schemas.map((s) => {
          const v = value[s.key] ?? "";
          const testid = `cf-${s.key}`;
          return (
            <div key={s.id} className="space-y-1" data-testid={`cf-wrap-${s.key}`}>
              <Label className="text-[11px] flex items-center gap-1.5">
                {s.label}
                {s.required && <span className="text-crimson">*</span>}
                <span className="text-[9px] font-mono uppercase text-muted-foreground">{s.field_type}</span>
              </Label>
              {s.field_type === "text" && (
                <Input value={v} onChange={(e) => setKey(s.key, e.target.value)} className="rounded-lg h-9" data-testid={testid} />
              )}
              {s.field_type === "url" && (
                <Input type="url" value={v} onChange={(e) => setKey(s.key, e.target.value)} className="rounded-lg h-9" placeholder="https://" data-testid={testid} />
              )}
              {s.field_type === "number" && (
                <Input type="number" value={v} onChange={(e) => setKey(s.key, e.target.value === "" ? "" : Number(e.target.value))} className="rounded-lg h-9" data-testid={testid} />
              )}
              {s.field_type === "date" && (
                <Input type="date" value={v} onChange={(e) => setKey(s.key, e.target.value)} className="rounded-lg h-9" data-testid={testid} />
              )}
              {s.field_type === "boolean" && (
                <div className="flex items-center h-9">
                  <Switch checked={!!v} onCheckedChange={(x) => setKey(s.key, x)} data-testid={testid} />
                </div>
              )}
              {s.field_type === "select" && (
                <Select value={v ? String(v) : ""} onValueChange={(nv) => setKey(s.key, nv)}>
                  <SelectTrigger className="rounded-lg h-9" data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(s.options || []).map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
